import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Typography,
  alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion } from 'framer-motion';
import { Link } from 'react-router';

import { MedirVisita } from '@/components/MedirVisita';

import type { Route } from './+types/marcas';

/**
 * Las marcas, con el texto tal cual está publicado.
 *
 * Antes cada descripción era un solo string que la pantalla partía por saltos de línea para
 * poner en negrita la primera frase. Acá el título y el cuerpo están separados desde el
 * principio: sale lo mismo, y se entiende de una lectura.
 *
 * La lista de productos que traía cada marca no se copió: estaba en los datos y no se dibujaba
 * en ningún lado.
 */
const MARCAS = [
  {
    nombre: 'HADA',
    logo: '/img/marcas/hada-logo.webp',
    titulo: 'Máxima calidad en repuestos de motos',
    texto:
      'Una amplia línea de repuestos desarrollada para brindar calidad, confianza y ' +
      'rendimiento. Todo lo que tu moto necesita.',
  },
  {
    nombre: 'HAFEEN',
    logo: '/img/marcas/hafeen-logo.webp',
    titulo: 'Máxima calidad en componentes de motor de alto rendimiento',
    texto:
      'Marca especializada en cilindros, pistones y otros componentes de motor, reconocida por ' +
      'la calidad, precisión y confiabilidad de sus productos.',
  },
  {
    nombre: 'VTC',
    logo: '/img/marcas/vtc-logo.webp',
    titulo: 'Tecnología y calidad de primera línea',
    texto:
      'Proveedor en China de marcas líderes como Honda, Yamaha, Lifan, Rato, Briggs & Stratton ' +
      'y Robin. Calidad y experiencia que hacen de VTC una elección ideal para tu moto.',
  },
];

export function meta(): Route.MetaDescriptors {
  return [
    { title: 'Nuestras Marcas | CKING Repuestos para Motos' },
    {
      name: 'description',
      content:
        'HADA, HAFEEN y VTC: las marcas que CKING importa y distribuye en Argentina. ' +
        'Repuestos y componentes de motor para motocicletas.',
    },
    { tagName: 'link', rel: 'canonical', href: 'https://cking.com.ar/marcas' },
  ];
}

export default function Marcas() {
  return (
    <>
      <MedirVisita path="/marcas" />

      <Box
        sx={{
          py: 6,
          bgcolor: '#0a0a0a',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/marcas.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.2,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h2"
              component="h1"
              align="center"
              sx={{ mb: 2, fontWeight: 700, textTransform: 'uppercase' }}
            >
              Nuestras Marcas
            </Typography>

            <Typography
              variant="h5"
              component="p"
              align="center"
              sx={{ mb: 4, maxWidth: 800, mx: 'auto', color: 'rgba(255, 255, 255, 0.8)' }}
            >
              Trabajamos con las mejores marcas del mercado para ofrecerte la más alta calidad en
              repuestos y accesorios para tu motocicleta.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4} sx={{ justifyContent: 'center' }}>
          {MARCAS.map((marca, indice) => (
            <Grid key={marca.nombre} sx={{ width: '100%' }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: indice * 0.1 }}
              >
                <Card
                  sx={tema => ({
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    width: '100%',
                    bgcolor: '#1a1a1a',
                    border: '1px solid transparent',
                    transition:
                      'transform .35s cubic-bezier(.22,.61,.36,1), box-shadow .35s ease, border-color .35s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 14px 30px rgba(0,0,0,.45)',
                      borderColor: alpha(tema.palette.primary.main, 0.35),
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                      transition: 'border-color .35s ease',
                      '&:hover': { transform: 'none' },
                    },
                  })}
                >
                  <CardMedia
                    component="img"
                    image={marca.logo}
                    alt={marca.nombre}
                    sx={{
                      width: { xs: '100%', sm: 220 },
                      // Alto fijo y no 'auto'. Con auto cada logo decidía el alto de su tarjeta:
                      // HAFEEN es cuadrado y estiraba la suya al doble, HADA es apaisado y la
                      // dejaba chata, y las tres quedaban de distinto porte una debajo de otra.
                      height: { xs: 180, sm: 170 },
                      objectFit: 'contain',
                      flexShrink: 0,
                      // Blanco: dos de los tres logos ya vienen con fondo blanco y sobre el gris
                      // se veían como un recuadro pegado encima. Sobre blanco los tres se leen
                      // igual, que es de lo que se trata una página de marcas.
                      bgcolor: '#fff',
                      p: 2.5,
                    }}
                  />

                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: 0.75,
                      py: 3,
                    }}
                  >
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                      {marca.nombre}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '.02em' }}
                    >
                      {marca.titulo}
                    </Typography>

                    {/* Ancho de lectura acotado. A todo lo ancho de la tarjeta las líneas
                        pasaban los cien caracteres y el ojo perdía el renglón al volver. */}
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', maxWidth: '64ch', lineHeight: 1.7 }}
                    >
                      {marca.texto}
                    </Typography>

                    {/* El link que faltaba: se contaba la marca y no había por dónde entrar a
                        sus piezas. Es la puerta al catálogo filtrado y, para un buscador, lo que
                        ata esta página con los productos de esa marca. */}
                    <Button
                      component={Link}
                      to={`/catalogo?brand=${encodeURIComponent(marca.nombre)}`}
                      variant="outlined"
                      color="primary"
                      sx={{ alignSelf: 'flex-start', mt: 1 }}
                    >
                      Ver repuestos {marca.nombre}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  );
}
